// MAJLIS-64 (D-43, D-08, D-17, D-38, EM-25, UI-C6, T-305; proposal sections 4, 5, 7): the phone's VOICE QUEUE.
//
// Interpreter mode is the UN earpiece: on THIS phone, each other person's phrase is SPOKEN in the reader's language,
// one at a time, in the order the phrases STARTED (MAJLIS-119), with the live voices lowered underneath. The queue
// lives here, on the phone; the server keeps none and only answers pulls (consultant's binding ruling):
//  - WHO QUEUES: listen mode `translated`, the room `live`, Listen ON, and room_state.voice true - and the whole
//    auto-play path is behind VOICE_QUEUE_ENABLED (constants.ts), ON since the owner approved the UI. In a
//    screen-only room or with Listen off nothing auto-plays (G7/G8: people sitting together would hear each other's
//    phones). A phrase is PLAYED only if it is FINAL (D-17), is ANOTHER seat's (G6), is in another language than the
//    reader's (D-43(5)), and its translation arrived for the reading language while the queue was on;
//  - PULL AT THE HEAD (D-08): POST /api/voice (seat token in the header) is sent only when a READY phrase reaches the
//    head and starts - never earlier - so a phrase skipped by catch-up is never requested and costs nothing, and a
//    phrase WAITING for its translation (ORDER below) has not been pulled: the wait costs nothing, and a phrase skipped
//    by the wait is pulled only if its translation lands inside catch-up and it then reaches the head. The answer's
//    one-clip URL is played in ONE persistent <audio> element (plain element, MP3, no MediaSource, no cookie);
//  - ORDER (G5, MAJLIS-119, consultant ruling (a), 2026-09-25): phrase START order (t0, P-18/D-24), each phrase at most
//    once. Measured on the owner's replay room (Toronto dev box, 2026-09-25): arrival order follows when a phrase ENDS
//    (105 inversions vs t0, 30 vs t1), 76 of 81 adjacent swaps were ACROSS two overlapping seats (within a seat A 3/180,
//    B 5/291), TTS p50/p95 205/354 ms is not the cause, translation ready after phrase end p50 -0.10 s, p95 1.62 s.
//    So the queue holds the pending phrases ordered by t0 and the head is the earliest-started phrase not yet voiced. A
//    phrase ENTERS the order at its speech_start (P-18 mints the id and t0 at the VAD onset, before its words or its
//    translation exist; the `segment` event enters one whose start was not seen, and a translation of one unknown to the
//    order is placed by its segment's t0). It LEAVES without a sound when it turns out not to be voiceable: dropped,
//    failed, this seat's, or in the reader's own language (its segment / revision says). If the head has no translation
//    yet while a later phrase is ready and the queue is free, the queue waits up to VOICE_ORDER_WAIT_MS (ASSUMPTION from
//    that p95), then skips every waiting phrase ahead of the first ready one and plays it. A skipped phrase is voiced
//    when its translation lands if that is within VOICE_CATCHUP_MS of the moment it was due (the wait's start), in its
//    t0 place (so next); later than that it is dropped, as catch-up drops;
//  - CATCH-UP (D-43(3), G8): a ready head older than VOICE_CATCHUP_MS when it would start (from its translation's
//    arrival, or for a skipped phrase from when it was due) collapses the backlog to the NEWEST (latest-started) ready
//    phrase; the phrases still waiting after it stay, the ones waiting before it count as skipped (due now), and the
//    phrase that is playing always finishes;
//  - THE FLOOR (D-43(4), UI-C24, G9): the person's mic tap stops the voice AT ONCE and drops the backlog; the queue
//    stays quiet while he speaks and resumes from the newest phrase once he has stopped (VOICE_FLOOR_QUIET_MS);
//  - TAP VOICE (UI-C6): any phrase plays now in the queue's element; the queue pauses and resumes with its head after.
//    Exactly one sound at a time (T-305): a tap also stops a replay (`stopOthers`), and a replay (D-38) pre-empts the
//    voice - the gate's `replay` hold stops it and holds the queue until the replay's span is over (proposal 4.6);
//  - THE GATE (EM-25, ruling A, load-bearing): the browser's echo canceller does not cover an HTTP <audio> element, so
//    the voice takes the microphone gate's `voice` hold at play() and releases it on ended / pause / error, the start
//    fuse, the watchdog, a skip, a stop and reset - the microphone can never stay shut because a clip went wrong;
//  - FAILURE: a 404, a 5xx, a network error or an element error skips that phrase (its text is untouched); 429
//    voice_capped stops AUTO-play for this room (tap still works) and says so through `onCapped` - a quiet state;
//  - EXITS (proposal 4.9): leaving interpreter mode, Listen off, screen-only, voicing off, or leaving the room empties
//    the queue at once and stops the voice it was playing.
// MAJLIS-137 (D-57, UI-C39; passive mode phase 1, PROPOSED 2026-09-29): in a PASSIVE room the one seat's phrases ARE the
// room (the phone on the table hears everyone), so they are voiced like another seat's (G6 does not apply there, and the
// server agrees), and the queue auto-plays exactly while the person's "Auto-play translations" switch is ON (off by
// default) and voicing is on - Listen, the listen mode and the audio mode play no part (the bar has none of them). The
// same order, catch-up, one-sound and failure rules hold. OWNER RULE (2026-09-29): in a passive room the microphone
// NEVER pauses or mutes itself while a translation plays - the voice does not take the gate's `voice` hold there (tap or
// auto-play); the browser's echo cancellation stays on (media/mic.ts) with this playback as its reference - the platform
// AEC is what keeps the phone from transcribing its own voice. There is no echo guard (owner ruling); measured in phase 1.
// MAJLIS-140 (D-59, P-39, EM-20f; PROPOSED 2026-09-29): in a PASSIVE room there is NO MP3 PATH (ruling c). The plain
// <audio> element was not cancelled by the owner's phones' echo canceller - the phone heard its own translation and
// transcribed it again - so the server plays a passive seat's clip on a LiveKit track, `voice-<seat id>`, which reaches
// the room's remote-voice element (media/hearing.ts, D-22) and so the canceller's far-end reference. The queue is the
// same (order, catch-up, one sound, pull at the head); only the playing differs: POST /api/voice answers
// `{"track": true}`, and the clip's `voice_track` messages say when it plays, ends or fails (the fuse and the watchdog
// as for an element). The track is SUBSCRIBED only while the switch is ON or a tapped clip plays (`track.want`;
// condition 3), and a request waits until it is (`track.ready`). A failure is never silent: the switch shows its line
// (`onTrackFailed`) and the server, or - for its own failures - the phone, writes the gap record (condition 5).
// What the room shows reads it here (UI-C21..C24, UI-C6): `speaking()` the highlight, `pending()` the Voice button's
// wait, `behind()` the pill, `capped()` the quiet line, `autoPlay()` whether interpreter mode can be offered at all.
import type { Clock, TimerId } from "../clock";
import {
  VOICE_CATCHUP_MS,
  VOICE_FLOOR_QUIET_MS,
  VOICE_MAX_HOLD_MS,
  VOICE_ORDER_WAIT_MS,
  VOICE_PLAYED_MAX_MS,
  VOICE_START_FUSE_MS,
} from "../constants";
import { sameLanguage } from "../data/languages";
import type { ListenMode, RoomEvent, TranslationData, VoiceTrackData } from "../protocol/types";
import type { HoldKind } from "./micGate";
import type { AudioLike } from "./replay";

export const VOICE_PATH = "/api/voice";

/** The subset of the <audio> element the queue drives (replay's AudioLike: tests inject the same fake). */
export type VoiceAudio = Pick<AudioLike, "src" | "onended" | "onerror" | "onpause" | "play" | "pause" | "removeAttribute" | "load">;

export interface VoiceQueueOptions {
  fetch: typeof fetch;
  /** The seat token for the Authorization header (never a URL, never a log); null before the seat is joined. */
  seatToken: () => string | null;
  /** Creates the ONE element every voice plays through (called once, lazily; kept for the queue's life). */
  audio: () => VoiceAudio;
  clock: Clock;
  /** VOICE_QUEUE_ENABLED, injected so the tests can run the queue with it on. Off: nothing auto-plays, ever. */
  enabled: boolean;
  /** EM-25 (ruling A): the microphone gate; the voice holds it with kind `voice`. */
  gate?: { acquire(kind?: HoldKind): () => void };
  /** Exactly one sound at a time (T-305): stop anything else this phone plays (the replay) before a tapped voice. */
  stopOthers?: () => void;
  /** 429 voice_capped: auto-play has stopped for this room (the model's quiet `voiceCapped`). */
  onCapped?: () => void;
  /** A play() the browser refused (no gesture yet): true until a tap unlocks the element (the app's tap line). */
  onWaitingChange?: (waiting: boolean) => void;
  /** Diagnostics (Q-1: the failure with its body). Never receives the token or a clip URL. */
  log?: (message: string, body?: unknown) => void;
  /** MAJLIS-140 (P-39): a passive seat's voice track (media/voiceTrack.ts) - subscribed while wanted, and ready (the
   * subscription in place) before a request goes out. Absent: no passive clip is ever requested. */
  track?: { want(on: boolean): void; ready(): Promise<boolean> };
  /** MAJLIS-140 (condition 5): a clip on the voice track will not play. `phone`: the phone's own finding (no_start),
   * which the app reports to the server; otherwise the server's `voice_track` failed (it has recorded it itself). */
  onTrackFailed?: (reason: string, segmentId: string, phone: boolean) => void;
  /** MAJLIS-144 (P-39, T-166): a clip on the voice track started playing - once per clip, with the phone's own ms from
   * the seat's request to that moment (whole ms, clamped to 0..VOICE_PLAYED_MAX_MS). The app sends `voice_played`. */
  onTrackPlaying?: (segmentId: string, requestToPlayingMs: number) => void;
}

/** Everything outside the queue that decides whether it auto-plays, and for whom. */
export interface VoiceConditions {
  mySeatId: string | null;
  readLang: string;
  listenMode: ListenMode;
  /** room_state / audio_mode_changed: the room is `live` (not screen-only). */
  liveAudio: boolean;
  /** This seat's Listen, as the server recorded it. */
  listening: boolean;
  /** room_state.voice. */
  voiceOn: boolean;
  /** MAJLIS-137: room_state.passive - the seat's own phrases are voiced, and `autoPlay` alone decides auto-play. */
  passive: boolean;
  /** MAJLIS-137 (UI-C39): the person's "Auto-play translations" switch (a passive room only; OFF by default). */
  autoPlay: boolean;
}

interface Item {
  segmentId: string;
  lang: string;
  /** the injected clock when its translation arrived here (for a phrase skipped by the order wait: when it was due) */
  arrivedAt: number;
}

/** MAJLIS-119: one pending phrase in the t0 order; `item` is set once its translation is here (READY). */
interface Pending {
  segmentId: string;
  t0: number;
  item: Item | null;
}

interface Current {
  item: Item;
  why: "queue" | "tap";
  gen: number;
  phase: "requesting" | "playing";
  /** set once the clip is on the element */
  url?: string;
  /** MAJLIS-140: the clip is on the passive seat's voice track (the answer said so); when it was asked for */
  track?: boolean;
  askedAt?: number;
  ungate?: () => void;
  fuse?: TimerId;
  watchdog?: TimerId;
}

interface SegmentInfo {
  seat: string;
  lang: string;
  final: boolean;
  t0: number;
  /** known before the latest join / resume / reading-language change: history, never voiced by the queue */
  history: boolean;
}

export class VoiceQueue {
  private cond: VoiceConditions = {
    mySeatId: null,
    readLang: "",
    listenMode: "translated",
    liveAudio: false,
    listening: false,
    voiceOn: false,
    passive: false,
    autoPlay: false,
  };
  /** MAJLIS-119: the pending phrases in t0 order, waiting (no translation yet) or ready */
  private items: Pending[] = [];
  /** skipped by the order wait, not yet translated: segment id -> when it was due (the wait's start) */
  private skipped = new Map<string, number>();
  /** the order wait on the head: since when a ready phrase has been held behind it */
  private wait: { segmentId: string; since: number; timer?: TimerId } | null = null;
  /** every segment the queue has ever taken (queued, played, tapped or skipped): never twice (G5) */
  private seen = new Set<string>();
  private segments = new Map<string, SegmentInfo>();
  /** THIS seat's phrases still open (speech_start without its close): the floor's "still speaking" */
  private ownOpen = new Set<string>();
  private current: Current | null = null;
  private gen = 0;
  private el: VoiceAudio | null = null;
  private unlocked = false;
  private waiting = false;
  private replaying = false;
  private floor = false;
  private floorTimer: TimerId | undefined;
  private isCapped = false;
  private trackWanted = false;
  private ver = 0;
  private listeners = new Set<() => void>();

  constructor(private readonly opts: VoiceQueueOptions) {}

  // ---------------------------------------------------------------- what the (later) UI reads

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  version = (): number => this.ver;

  /** UI-C23 "N behind": phrases queued and not yet started (ready ones: a phrase still waiting for its translation, or
   * still being said, is not behind). */
  behind(): number {
    return this.items.filter((p) => p.item).length;
  }

  /** UI-C22: the phrase whose voice is sounding now (null while requesting or idle). */
  speaking(): string | null {
    return this.current?.phase === "playing" ? this.current.item.segmentId : null;
  }

  /** UI-C6: the phrase being voiced now, from the tap or the queue's pull until its sound stops (requesting included):
   * its Voice button waits instead of asking a second time. */
  pending(): string | null {
    return this.current?.item.segmentId ?? null;
  }

  /** Whether this build auto-plays at all (VOICE_QUEUE_ENABLED): off, interpreter mode is not offered on screen. */
  autoPlay(): boolean {
    return this.opts.enabled;
  }

  /** 429 voice_capped seen in this room: text only, voice on tap. */
  capped(): boolean {
    return this.isCapped;
  }

  /** A voice is on the element now (sounding, or about to): the mic button's tap is then UI-C24's "stop and open". */
  sounding(): boolean {
    return this.current?.url !== undefined;
  }

  /** Whether the queue would auto-play now (the flag and every condition). */
  active(): boolean {
    const c = this.cond;
    if (c.passive) return this.opts.enabled && !this.isCapped && c.autoPlay && c.voiceOn && c.mySeatId !== null;
    return (
      this.opts.enabled &&
      !this.isCapped &&
      c.listenMode === "translated" &&
      c.liveAudio &&
      c.listening &&
      c.voiceOn &&
      c.mySeatId !== null
    );
  }

  // ---------------------------------------------------------------- inputs

  /** The app's state changed. Any exit (interpreter mode left, Listen off, screen-only, voicing off) empties the queue
   * and stops the voice it was playing; a change of reading language drops what was queued in the old one. */
  configure(next: Partial<VoiceConditions>): void {
    const wasActive = this.active();
    const langChanged = next.readLang !== undefined && next.readLang !== this.cond.readLang;
    this.cond = { ...this.cond, ...next };
    if (langChanged) {
      this.dropQueued();
      this.markHistory(); // the server re-sends and backfills translations into the new language: not live phrases
    }
    if (wasActive && !this.active()) this.empty("the queue is off");
    else this.pump();
    this.syncTrack();
  }

  /** joined / resumed: every phrase known so far (a resume replays the missed ones BEFORE its `resumed`) is history. */
  resync(): void {
    this.markHistory();
    // MAJLIS-119: phrases still waiting were replayed before this: history too (a live one's translation is placed by
    // its segment's t0 when it lands)
    const before = this.items.length;
    this.items = this.items.filter((p) => p.item);
    this.skipped.clear();
    if (this.items.length !== before) {
      this.clearWait();
      this.pump();
    }
  }

  /** A room event (the queue tracks phrases itself: seat, language, final, and this seat's own speech for the floor). */
  onEvent(ev: RoomEvent): void {
    switch (ev.type) {
      case "speech_start":
        if (ev.data.seat === this.cond.mySeatId && !this.cond.passive) {
          this.ownOpen.add(ev.data.segment_id);
          if (this.floor) this.clearFloorTimer(); // he is speaking: stay quiet
        } else this.enter(ev.data.segment_id, ev.data.t0); // MAJLIS-119: known by t0 before its words exist
        return;
      case "speech_end":
        return this.ownClosed(ev.data.segment_id);
      case "segment_dropped":
      case "segment_failed":
        this.leave(ev.data.segment_id); // never a translation: it must not hold the order
        return this.ownClosed(ev.data.segment_id);
      case "segment": {
        const s = ev.data;
        this.segments.set(s.id, { seat: s.seat, lang: s.lang, final: s.state === "final", t0: s.t0, history: false });
        if (this.voiceable(s.seat, s.lang)) this.enter(s.id, s.t0);
        else this.leave(s.id);
        return this.ownClosed(s.id);
      }
      case "revision": {
        const info = this.segments.get(ev.data.id);
        if (info) {
          this.segments.set(ev.data.id, { ...info, lang: ev.data.lang, final: true });
          if (!this.voiceable(info.seat, ev.data.lang)) this.leave(ev.data.id);
        }
        return;
      }
      default:
        return;
    }
  }

  /** P-10: a translation arrived. Queued only if every rule of the header holds. */
  onTranslation(d: TranslationData): void {
    if (!this.active()) return;
    const c = this.cond;
    if (d.lang !== c.readLang || this.seen.has(d.segment_id)) return;
    const info = this.segments.get(d.segment_id);
    if (!info || info.history || !info.final) return; // D-17: final only; history is not a live phrase
    if (info.seat === c.mySeatId && !c.passive) return; // G6: never this seat's own phrase (MAJLIS-137: but in passive)
    if (sameLanguage(info.lang, c.readLang)) return; // D-43(5): the reader's own language is heard live
    const now = this.opts.clock.now();
    let arrivedAt = now;
    const due = this.skipped.get(d.segment_id);
    if (due !== undefined) {
      // MAJLIS-119: skipped by the order wait; voiced only if it lands inside catch-up, counted from when it was due
      this.skipped.delete(d.segment_id);
      if (now - due > VOICE_CATCHUP_MS) {
        this.seen.add(d.segment_id);
        return this.log("voice: a skipped phrase landed too late, dropped", {
          segment: d.segment_id,
          late_ms: now - due,
          assumption_ms: VOICE_CATCHUP_MS,
        });
      }
      arrivedAt = due;
    }
    this.seen.add(d.segment_id);
    const item: Item = { segmentId: d.segment_id, lang: d.lang, arrivedAt };
    const at = this.items.find((p) => p.segmentId === d.segment_id);
    if (at) at.item = item;
    else this.insert({ segmentId: d.segment_id, t0: info.t0, item });
    this.emit();
    this.pump();
  }

  /** MAJLIS-119: a phrase of another seat joins the t0 order (waiting) while the queue is on; once only. */
  private enter(segmentId: string, t0: number): void {
    if (!this.active() || this.seen.has(segmentId) || this.skipped.has(segmentId)) return;
    if (this.items.some((p) => p.segmentId === segmentId)) return;
    this.insert({ segmentId, t0, item: null });
    this.pump();
  }

  /** MAJLIS-119: a phrase that will never be voiced here leaves the order (a waiting head stops holding the queue). */
  private leave(segmentId: string): void {
    this.skipped.delete(segmentId);
    const i = this.items.findIndex((p) => p.segmentId === segmentId && !p.item);
    if (i < 0) return;
    this.items.splice(i, 1);
    if (this.wait?.segmentId === segmentId) this.clearWait();
    this.pump();
  }

  private voiceable(seat: string, lang: string): boolean {
    return (this.cond.passive || seat !== this.cond.mySeatId) && !sameLanguage(lang, this.cond.readLang);
  }

  /** In t0 order; a tie goes after the phrases already there. */
  private insert(p: Pending): void {
    let i = this.items.length;
    while (i > 0 && this.items[i - 1].t0 > p.t0) i -= 1;
    this.items.splice(i, 0, p);
  }

  /** UI-C6: tap Voice on any phrase - it plays NOW in the queue's element; the queue resumes with its head after.
   * Called from inside the tap, so it also unlocks the element. Not behind the flag: this is a person's request. */
  tapVoice(segmentId: string, lang: string): void {
    this.unlockElement(); // not unlock(): its pump would start the head, a paid request the tap then throws away
    if (!this.cond.voiceOn) return this.log("voice: voicing is off in this deployment", { segment: segmentId });
    this.opts.stopOthers?.(); // T-305: one sound at a time
    this.stopCurrent("a tapped phrase takes over");
    this.items = this.items.filter((i) => i.segmentId !== segmentId);
    this.skipped.delete(segmentId);
    if (this.wait?.segmentId === segmentId) this.clearWait();
    this.seen.add(segmentId);
    this.start({ segmentId, lang, arrivedAt: this.opts.clock.now() }, "tap");
  }

  /** D-43(4), UI-C24: the person tapped the mic. The voice stops AT ONCE, the backlog is dropped, and the queue stays
   * quiet until he has stopped speaking; then it resumes from the newest phrase. */
  micTap(): void {
    this.stopCurrent("the mic was tapped");
    this.dropQueued();
    this.floor = true;
    this.armFloorTimer();
    this.pump();
  }

  /** UI-C23's 'catch up' tap: stop the current voice and jump to the newest phrase. */
  catchUp(): void {
    if (this.current?.why === "queue") this.stopCurrent("catch up");
    this.collapseToNewest();
    this.pump();
  }

  /** D-38 (proposal 4.6): a replay pre-empts the voice. Driven by the gate's `replay` kind: when a replay starts, the
   * voice stops (that phrase counts as heard) and the queue waits until the replay's span (clip plus tail) is over. */
  setReplaying(on: boolean): void {
    if (on === this.replaying) return;
    this.replaying = on;
    if (on) this.stopCurrent("a replay started");
    else this.pump();
  }

  /** From inside a user gesture (the Listen tap, any tap in the room): unlock the one element for the programmatic
   * play() calls that follow. ASSUMPTION (iOS Safari), as for replay: play() inside the tap unlocks it, and it stays
   * unlocked when its src changes - UNMEASURED, to be checked on the owner's iPhone. */
  unlock(): void {
    this.unlockElement();
    this.pump();
  }

  /** MAJLIS-140 (P-39): a `voice_track` message about this seat's clip on its voice track. */
  onVoiceTrack(d: VoiceTrackData): void {
    const c = this.current;
    if (!c || !this.cond.passive || d.segment_id !== c.item.segmentId || d.cut) return; // a cut clip is never current
    const gen = c.gen;
    if (d.state === "playing") {
      if (c.phase === "playing") return;
      this.clearFuse(c);
      c.phase = "playing";
      c.watchdog = this.opts.clock.setTimeout(() => this.finish(gen, "voice: the track clip never ended"), VOICE_MAX_HOLD_MS);
      // condition 6, at the phone: request -> the server's first frame reported here (the audio's own playout after it
      // is the WebRTC jitter buffer, measured on the phone)
      const ms = c.askedAt === undefined ? null : this.opts.clock.now() - c.askedAt;
      this.log("voice: track clip playing", { segment: d.segment_id, request_to_playing_ms: ms });
      // MAJLIS-144 (T-166): the phone's own number goes to the server, once per clip (the guard above)
      if (ms !== null) this.opts.onTrackPlaying?.(d.segment_id, Math.min(VOICE_PLAYED_MAX_MS, Math.max(0, Math.round(ms))));
      this.emit();
    } else if (d.state === "ended") {
      this.finish(gen, null);
    } else {
      this.opts.onTrackFailed?.(d.reason ?? "unknown", d.segment_id, false);
      this.finish(gen, "voice: the voice track could not play the clip", { reason: d.reason ?? null });
    }
  }

  private unlockElement(): void {
    if (this.cond.passive) {
      // MAJLIS-140 (ruling c): a passive seat has no MP3 element; its voice is a remote track, which Hearing unlocks
      this.unlocked = true;
      this.setWaiting(false);
      return;
    }
    const el = this.element();
    if (!this.unlocked && !this.current) el.play().catch(() => undefined);
    this.unlocked = true;
    this.setWaiting(false);
  }

  /** Leaving the room: stop, forget every phrase, the floor and the cap. The element and its unlock are kept. */
  reset(): void {
    this.stopCurrent("reset");
    this.items = [];
    this.skipped.clear();
    this.clearWait();
    this.seen.clear();
    this.segments.clear();
    this.ownOpen.clear();
    this.floor = false;
    this.clearFloorTimer();
    this.isCapped = false;
    this.replaying = false;
    this.emit();
  }

  // ---------------------------------------------------------------- the queue

  private pump(): void {
    if (this.current || !this.active() || this.replaying || this.floor || !this.unlocked) return;
    const ready = this.items.findIndex((p) => p.item);
    if (ready < 0) return this.clearWait(); // nothing to play: a waiting head holds nobody back
    const now = this.opts.clock.now();
    if (ready > 0) {
      // MAJLIS-119: the head has no translation yet and a later phrase is ready: wait for the head, but not for long
      const head = this.items[0].segmentId;
      if (this.wait?.segmentId !== head) {
        this.clearWait();
        const wait: { segmentId: string; since: number; timer?: TimerId } = { segmentId: head, since: now };
        wait.timer = this.opts.clock.setTimeout(() => {
          wait.timer = undefined;
          this.pump();
        }, VOICE_ORDER_WAIT_MS);
        this.wait = wait;
        return;
      }
      if (now - this.wait.since < VOICE_ORDER_WAIT_MS) return;
      const passed = this.items.splice(0, ready);
      for (const p of passed) this.skipped.set(p.segmentId, this.wait.since);
      this.log("voice: order wait over, playing the next ready phrase", {
        skipped: passed.length,
        assumption_ms: VOICE_ORDER_WAIT_MS,
      });
    }
    this.clearWait();
    if (now - this.items[0].item!.arrivedAt > VOICE_CATCHUP_MS) this.collapseToNewest();
    const item = this.items.shift()!.item!;
    this.emit();
    this.start(item, "queue");
  }

  /** Keep only the newest (latest-started) READY phrase and the phrases still waiting after it. The ready backlog before
   * it is never voiced by the queue; a phrase before it still WAITING for its translation is treated as skipped by the
   * order wait, due now (voiced when it lands only if inside catch-up), so a long phrase still being said is not lost. */
  private collapseToNewest(): void {
    let newest = -1;
    for (let i = 0; i < this.items.length; i++) if (this.items[i].item) newest = i;
    if (newest <= 0) return;
    const now = this.opts.clock.now();
    const dropped = this.items.splice(0, newest);
    for (const p of dropped) if (!p.item) this.skipped.set(p.segmentId, now);
    this.clearWait();
    const skipped = dropped.filter((p) => p.item).length;
    this.log("voice: catching up to the newest phrase", { skipped, assumption_ms: VOICE_CATCHUP_MS });
    this.emit();
  }

  private clearWait(): void {
    if (this.wait?.timer !== undefined) this.opts.clock.clearTimeout(this.wait.timer);
    this.wait = null;
  }

  private start(item: Item, why: "queue" | "tap"): void {
    const gen = ++this.gen;
    this.current = {
      item,
      why,
      gen,
      phase: "requesting",
      fuse: this.opts.clock.setTimeout(() => this.fuseBlown(gen), VOICE_START_FUSE_MS),
    };
    this.emit();
    void this.load(item, gen);
  }

  private async load(item: Item, gen: number): Promise<void> {
    const id = item.segmentId;
    const token = this.opts.seatToken();
    if (!token) return this.skip(gen, "voice: no seat yet", {});
    const passive = this.cond.passive;
    if (passive) {
      // MAJLIS-140: the voice track must be subscribed before the clip goes out (its failure is reported by the track)
      if (!this.opts.track) return this.skip(gen, "voice: no voice track on this phone", {});
      const ready = await this.opts.track.ready();
      if (gen !== this.gen) return;
      if (!ready) return this.skip(gen, "voice: the voice track is not subscribed", {});
      if (this.current) this.current.askedAt = this.opts.clock.now();
    }
    let res: Response;
    try {
      res = await this.opts.fetch(VOICE_PATH, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ segment_id: id, lang: item.lang }),
        cache: "no-store",
      });
    } catch (e) {
      return this.skip(gen, "voice: the request failed", { error: errorName(e) });
    }
    if (gen !== this.gen) return; // stopped, replaced or given up on while it was on its way
    if (res.status === 429) {
      const code = await readCode(res);
      if (gen !== this.gen) return;
      if (code === "voice_capped") return this.capAt(gen);
      return this.skip(gen, "voice: refused for now", { status: 429, code });
    }
    if (res.status !== 200) {
      // 404 is every refusal (voicing off, not final, no translation, own phrase...); a 5xx is a failure. Either way
      // the phrase is skipped and its text stands.
      return this.skip(gen, "voice: not available", { status: res.status, code: await readCode(res) });
    }
    let url: unknown;
    let track: unknown;
    try {
      ({ url, track } = (await res.json()) as { url?: unknown; track?: unknown });
    } catch (e) {
      return this.skip(gen, "voice: unreadable answer", { error: errorName(e) });
    }
    if (gen !== this.gen) return;
    if (passive) {
      // MAJLIS-140 (ruling c): a passive seat is NEVER played by URL - its clip is on its voice track; the fuse runs on
      if (track !== true) return this.skip(gen, "voice: a passive seat's answer named no voice track", {});
      this.current!.track = true;
      return;
    }
    if (typeof url !== "string" || !url.startsWith(`${VOICE_PATH}/`)) {
      return this.skip(gen, "voice: the answer carried no clip", {}); // never the value itself: it is a capability
    }
    this.play(url, gen);
  }

  private play(url: string, gen: number): void {
    const c = this.current!;
    const el = this.element();
    el.onended = () => this.finish(gen, null);
    // `pause` comes from outside (iOS: a call, Siri, a headset unplugged; never `ended` then) and also just before a
    // natural `ended`: either way the sound has stopped, so the gate opens and the queue moves on
    el.onpause = () => this.finish(gen, "voice: playback paused");
    el.onerror = () => this.finish(gen, "voice: the clip could not be played");
    el.src = url;
    c.url = url;
    // EM-25, ruling A: the gate goes on at play(), before the first sample, and comes off in release().
    // MAJLIS-137 (owner rule, 2026-09-29): NOT in a passive room - there the microphone never pauses for a translation.
    c.ungate = this.cond.passive ? undefined : this.opts.gate?.acquire("voice");
    c.watchdog = this.opts.clock.setTimeout(() => this.finish(gen, "voice: gate held too long, releasing it"), VOICE_MAX_HOLD_MS);
    el.play().then(
      () => {
        if (gen !== this.gen || !this.current) return;
        this.clearFuse(this.current);
        this.current.phase = "playing";
        this.emit();
      },
      (e: unknown) => {
        if (gen !== this.gen) return;
        const name = errorName(e);
        if (name === "NotAllowedError") {
          // no gesture has unlocked the element: nothing more is requested (each would be paid for and silent) until
          // a tap does; the app shows its tap line meanwhile
          this.unlocked = false;
          this.setWaiting(true);
        }
        this.finish(gen, "voice: the browser did not start the clip", { error: name });
      },
    );
  }

  private capAt(gen: number): void {
    this.log("voice: the spend cap is reached; auto-play stops, voice stays on tap", { status: 429 });
    const first = !this.isCapped;
    this.isCapped = true;
    this.dropQueued();
    this.finish(gen, null);
    if (first) this.opts.onCapped?.();
  }

  private fuseBlown(gen: number): void {
    const c = this.current;
    if (c && gen === this.gen && this.cond.passive && c.track) {
      // MAJLIS-140 (condition 5): the phone's own finding - nothing started on the track in time
      this.opts.onTrackFailed?.("no_start", c.item.segmentId, true);
    }
    this.finish(gen, "voice: the clip did not start in time", { assumption_ms: VOICE_START_FUSE_MS });
  }

  private skip(gen: number, message: string, body: Record<string, unknown>): void {
    this.finish(gen, message, body);
  }

  /** The voice `gen` is over (ended, failed, skipped): release everything it held and move on to the next. */
  private finish(gen: number, message: string | null, body: Record<string, unknown> = {}): void {
    if (gen !== this.gen || !this.current) return;
    if (message) this.log(message, { segment: this.current.item.segmentId, ...body });
    this.stopCurrent(null);
    this.pump();
  }

  /** Stop whatever voice plays or is being requested; release its gate hold and its timers. Never pumps. */
  private stopCurrent(reason: string | null): void {
    const c = this.current;
    this.gen += 1;
    if (!c) return;
    this.current = null;
    if (reason) this.log(`voice: stopped (${reason})`, { segment: c.item.segmentId });
    this.clearFuse(c);
    if (c.watchdog !== undefined) this.opts.clock.clearTimeout(c.watchdog);
    const el = this.el;
    if (el) {
      el.onended = null;
      el.onerror = null;
      el.onpause = null;
      el.pause();
      if (c.url) {
        el.removeAttribute("src");
        el.load();
      }
    }
    c.ungate?.();
    this.emit();
  }

  private empty(reason: string): void {
    this.dropQueued();
    this.floor = false;
    this.clearFloorTimer();
    if (this.current?.why === "queue") this.stopCurrent(reason);
  }

  private dropQueued(): void {
    this.skipped.clear();
    this.clearWait();
    if (this.items.length === 0) return;
    this.items = [];
    this.emit();
  }

  private markHistory(): void {
    for (const [id, info] of this.segments) if (!info.history) this.segments.set(id, { ...info, history: true });
  }

  // ---------------------------------------------------------------- the floor (D-43(4), G9)

  private ownClosed(segmentId: string): void {
    if (!this.ownOpen.delete(segmentId)) return;
    if (this.floor && this.ownOpen.size === 0) this.armFloorTimer();
  }

  private armFloorTimer(): void {
    this.clearFloorTimer();
    if (this.ownOpen.size > 0) return; // still speaking: re-armed when his phrase closes
    this.floorTimer = this.opts.clock.setTimeout(() => {
      this.floorTimer = undefined;
      this.floor = false;
      this.collapseToNewest(); // resume from the newest phrase
      this.pump();
    }, VOICE_FLOOR_QUIET_MS);
  }

  private clearFloorTimer(): void {
    if (this.floorTimer !== undefined) this.opts.clock.clearTimeout(this.floorTimer);
    this.floorTimer = undefined;
  }

  // ---------------------------------------------------------------- plumbing

  private clearFuse(c: Current): void {
    if (c.fuse === undefined) return;
    this.opts.clock.clearTimeout(c.fuse);
    c.fuse = undefined;
  }

  private setWaiting(on: boolean): void {
    if (on === this.waiting) return;
    this.waiting = on;
    this.opts.onWaitingChange?.(on);
  }

  private element(): VoiceAudio {
    this.el ??= this.opts.audio();
    return this.el;
  }

  private emit(): void {
    this.syncTrack();
    this.ver += 1;
    for (const fn of this.listeners) fn();
  }

  /** MAJLIS-140 (condition 3): the passive seat's voice track is subscribed exactly while the switch is ON (and the
   * queue would auto-play) or a tapped clip is being voiced; OFF, unsubscribed. */
  private syncTrack(): void {
    const want = this.cond.passive && (this.active() || this.current?.why === "tap");
    if (want === this.trackWanted) return;
    this.trackWanted = want;
    this.opts.track?.want(want);
  }

  private log(message: string, body?: unknown): void {
    this.opts.log?.(message, body);
  }
}

/** The refusal's code from its JSON body; never more than the code (a body could carry anything). */
async function readCode(res: Response): Promise<string | null> {
  try {
    const body = (await res.json()) as { code?: unknown };
    return typeof body.code === "string" ? body.code : null;
  } catch {
    return null;
  }
}

/** Only the error's name reaches a log: a message could carry anything. */
function errorName(e: unknown): string {
  return e instanceof Error ? e.name : typeof e;
}
